import { useEffect, useMemo, useState } from "react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Alert,
  Input,
  toast,
  cn,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";

/** Scale shape the dialog needs — a subset of `storePacks.listScales`' row. */
export interface ScaleOption {
  scaleId: string;
  name: string;
  labels: string[];
}

/** Pack shape for version/edit — a subset of `storePacks.list`' row. */
export interface PackForEdit {
  packId: string;
  name: string;
  scaleId: string;
  composition: Record<string, number>;
}

type Mode = "create" | "version" | "edit";

export interface PackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  scales: ScaleOption[];
  /** Required for `version`/`edit`. */
  pack?: PackForEdit | undefined;
  /** How many products/orders reference the pack (version mode Alert). */
  referencedNote?: string | undefined;
  onSaved: () => void;
  /** Opens the "New scale" dialog when the store has none yet. */
  onNeedScale: () => void;
}

/**
 * S-PK2 · New / version / edit pack dialog (spec 13, design 14).
 *
 * Name → size scale → per-size quantity grid → derived pieces. Composition is
 * local state over `Input`s, not a new primitive. Version mode is the only write
 * path for a referenced pack (R3); edit is in-place for unreferenced packs.
 */
export function PackDialog({
  open,
  onOpenChange,
  mode,
  scales,
  pack,
  referencedNote,
  onSaved,
  onNeedScale,
}: PackDialogProps) {
  const [name, setName] = useState("");
  const [scaleId, setScaleId] = useState<string>("");
  const [qty, setQty] = useState<Record<string, number>>({});
  /**
   * Has the operator touched a quantity yet?
   *
   * A fresh dialog has every size at 0, which is the same state as "you emptied
   * the run" — so without this, the prompt to fill something in greets you
   * before you have done anything wrong, reading as a validation failure you
   * already caused. Only after an edit is an empty composition worth flagging.
   */
  const [touched, setTouched] = useState(false);

  // Seed the form whenever the dialog opens. Version locks the scale to the
  // pack's own; create starts on the first scale, edit starts on the pack's but
  // may move off it (R3 — an unreferenced pack is editable, scale included).
  useEffect(() => {
    if (!open) return;
    setTouched(false);
    if (pack) {
      setName(pack.name);
      setScaleId(pack.scaleId);
      setQty({ ...pack.composition });
    } else {
      setName("");
      setScaleId(scales[0]?.scaleId ?? "");
      setQty({});
    }
  }, [open, pack, scales]);

  const scale = useMemo(() => scales.find((s) => s.scaleId === scaleId), [scales, scaleId]);
  const pieces = useMemo(
    () => Object.values(qty).reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0),
    [qty],
  );

  const create = trpc.storePacks.create.useMutation();
  const version = trpc.storePacks.version.useMutation();
  const edit = trpc.storePacks.edit.useMutation();
  const pending = create.isPending || version.isPending || edit.isPending;

  const composition = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [label, n] of Object.entries(qty)) if (n > 0) out[label] = n;
    return out;
  }, [qty]);

  /**
   * Sizes in the scale still on zero (R2 — a pack covers the whole curve).
   * Mirrors `assertValidComposition`'s completeness check so the operator is
   * told which boxes to fill instead of being refused by the server.
   */
  const missing = useMemo(
    () => (scale?.labels ?? []).filter((label) => !((qty[label] ?? 0) > 0)),
    [scale, qty],
  );

  /**
   * Re-base the grid onto another scale, keeping the quantity of any label the
   * two scales share (S→S, M→M) and dropping the rest.
   *
   * Pruning to the new scale's labels is not cosmetic: `composition` above emits
   * every `qty` key above zero, so a quantity left over from the old scale would
   * travel to the server and be refused by `assertValidComposition` as a size
   * that is not in this scale — naming a label the operator can no longer see.
   *
   * A switch that carries something over counts as touching the form: the
   * half-filled grid is now the operator's own doing, so the missing-sizes
   * prompt is help rather than an accusation. A switch that carries nothing
   * leaves a blank grid, which is a fresh start and not worth flagging.
   */
  function changeScale(nextScaleId: string) {
    const next = scales.find((s) => s.scaleId === nextScaleId);
    const carried: Record<string, number> = {};
    for (const label of next?.labels ?? []) {
      const n = qty[label] ?? 0;
      if (n > 0) carried[label] = n;
    }
    setScaleId(nextScaleId);
    setQty(carried);
    setTouched(Object.keys(carried).length > 0);
  }

  const title =
    mode === "create" ? "New pack" : mode === "version" ? `New version — ${pack?.name ?? ""}` : `Edit ${pack?.name ?? ""}`;
  const cta = mode === "version" ? "Create version" : mode === "edit" ? "Save changes" : "Create pack";

  async function submit() {
    if (!name.trim()) {
      toast.error("Name your pack");
      return;
    }
    if (missing.length > 0) {
      toast.error("Every size needs a quantity", {
        description: `${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} empty.`,
      });
      return;
    }
    try {
      if (mode === "create") {
        if (!scaleId) {
          toast.error("Pick a size scale");
          return;
        }
        await create.mutateAsync({ name: name.trim(), scaleId, composition });
        toast.success("Pack created");
      } else if (mode === "version") {
        await version.mutateAsync({ packId: pack!.packId, name: name.trim(), composition });
        toast.success("New version created");
      } else {
        if (!scaleId) {
          toast.error("Pick a size scale");
          return;
        }
        await edit.mutateAsync({ packId: pack!.packId, name: name.trim(), scaleId, composition });
        toast.success("Pack updated");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't save the pack", { description: storeErrorMessage(e) });
    }
  }

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Pieces of each size in one pack. Every size in the scale needs a quantity — a run
            that skips a size belongs on its own scale.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {mode === "version" && referencedNote ? (
            <Alert intent="info" description={referencedNote} />
          ) : null}

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">Pack name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard run"
              maxLength={32}
              autoFocus
            />
          </label>

          {/* Version is the write path for a REFERENCED pack, so its scale is
              fixed — re-basing mid-family would make the versions incomparable.
              Create and edit both pick freely (R3: unreferenced packs are
              editable, scale included). */}
          {mode !== "version" ? (
            scales.length === 0 ? (
              <Alert
                intent="warn"
                title="Create a size scale first"
                description="A pack is built on a scale of sizes — define one, then build the run."
              />
            ) : (
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">Size scale</span>
                <select
                  value={scaleId}
                  onChange={(e) => changeScale(e.target.value)}
                  className="h-9 rounded border border-line bg-paper px-2 text-sm text-ink"
                >
                  {scales.map((s) => (
                    <option key={s.scaleId} value={s.scaleId}>
                      {s.name} · {s.labels.join(", ")}
                    </option>
                  ))}
                </select>
              </label>
            )
          ) : (
            <p className="text-sm text-muted">
              Scale: <span className="font-medium text-ink">{scale?.name}</span>
            </p>
          )}

          {scale ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-ink">Composition</span>
              {/* Three columns, not four: `max` is 1000, so a cell has to hold
                  three digits without clipping. The min-width below is the hard
                  floor (3 characters + the input's own padding); the grid gives
                  more when the dialog is wider. */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {scale.labels.map((label) => (
                  <label key={label} className="flex items-center gap-2">
                    {/* Right-aligned so the label sits against its own field: at a
                        fixed left-aligned width a short label like "S" floats ~40px
                        from the box it names. `min-w` (not `w`) keeps the inputs in
                        vertical columns for ordinary scales while letting an
                        8-character label grow its cell instead of colliding. */}
                    <span className="min-w-10 shrink-0 text-right text-sm text-muted">
                      {label}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      max={1000}
                      inputMode="numeric"
                      aria-label={`Quantity — size ${label}`}
                      value={qty[label] ?? 0}
                      onChange={(e) => {
                        setTouched(true);
                        setQty((q) => ({ ...q, [label]: Math.max(0, Number(e.target.value) || 0) }));
                      }}
                      // Native spinners steal ~17px of an already tight cell and
                      // are useless for typing a run size — drop them.
                      className={cn(
                        "h-9 min-w-[calc(3ch+1.25rem)] px-2 text-right tabular-nums",
                        "[appearance:textfield]",
                        "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                      )}
                    />
                  </label>
                ))}
              </div>
              {/* `min-h` reserves the line so the grid doesn't jump between the
                  count and the missing-sizes prompt. Nothing shows until the
                  operator has touched a quantity — an untouched form is not a
                  mistake worth flagging. */}
              <div className="min-h-5 text-right text-sm">
                {missing.length === 0 && pieces > 0 ? (
                  <>
                    <span className="font-medium text-ink tabular-nums">{pieces}</span>{" "}
                    <span className="text-muted">
                      {pieces === 1 ? "piece" : "pieces"} per pack
                    </span>
                  </>
                ) : touched ? (
                  <span className="text-muted">
                    Every size needs a quantity — {missing.join(", ")}{" "}
                    {missing.length === 1 ? "is" : "are"} empty.
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {mode === "create" && scales.length === 0 ? (
            <Button intent="secondary" size="sm" onClick={onNeedScale}>
              New scale
            </Button>
          ) : null}
        </div>

        <DialogFooter>
          <Button intent="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            intent="primary"
            onClick={submit}
            disabled={pending || pieces === 0 || missing.length > 0}
          >
            {cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
